import { useState } from 'react';
import { Link } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ShieldCheck, Loader2, AlertOctagon, AlertTriangle, Info, CheckCircle2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const SEV = {
  fout: { label: 'Fouten', icon: AlertOctagon, cls: 'text-red-700 bg-red-50 border-red-200', dot: 'bg-red-500' },
  waarschuwing: { label: 'Waarschuwingen', icon: AlertTriangle, cls: 'text-amber-800 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  info: { label: 'Ter controle', icon: Info, cls: 'text-sky-800 bg-sky-50 border-sky-200', dot: 'bg-sky-500' },
};
const TYPE_LABEL = { uitgave: 'Uitgave', inkoop: 'Inkoopfactuur', factuur: 'Factuur', 'factuur derden': 'Factuur van derden' };

export const BoekhouderCheck = ({ startDate, endDate, readOnly = false }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  const run = async () => {
    if (!startDate || !endDate) { toast.error('Selecteer eerst een periode'); return; }
    setLoading(true);
    try {
      const r = await axiosInstance.get('/boekhouder/check', { params: { start: startDate, end: endDate } });
      setResult(r.data); setFilter('all');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Controle mislukt');
    } finally { setLoading(false); }
  };

  const issues = result ? result.issues.filter((x) => filter === 'all' || x.severity === filter) : [];
  const pctColor = !result ? '' : result.score.pct >= 90 ? 'text-emerald-700' : result.score.pct >= 60 ? 'text-amber-700' : 'text-red-700';

  return (
    <Card className="bg-white shadow-sm border-gray-200" data-testid="boekhouder-check-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-gray-800 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#1a5276]" /> Volledigheidscontrole
        </CardTitle>
        <p className="text-sm text-gray-500">Het systeem controleert alle uitgaven, inkoopfacturen en facturen in de gekozen periode op ontbrekende bonnen, nummers, btw-splitsingen, dubbele boekingen en concepten — zodat het dossier voor de boekhouder zo compleet mogelijk is.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={run} disabled={loading} className="bg-[#1a5276] hover:bg-[#154360]" data-testid="boekhouder-check-run-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Controleren...</> : <><ShieldCheck className="w-4 h-4 mr-2" /> Controleer periode {startDate} t/m {endDate}</>}
        </Button>

        {result && (
          <div className="space-y-4" data-testid="boekhouder-check-result">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="rounded-lg border border-gray-200 p-3 col-span-2 md:col-span-1">
                <p className="text-xs text-gray-500">Compleet</p>
                <p className={`text-3xl font-bold ${pctColor}`} data-testid="boekhouder-check-score">{result.score.pct}%</p>
                <p className="text-xs text-gray-500">{result.score.complete} van {result.score.total} documenten zonder fouten/waarschuwingen</p>
              </div>
              {Object.entries(SEV).map(([k, s]) => (
                <button type="button" key={k} onClick={() => setFilter(filter === k ? 'all' : k)} className={`rounded-lg border p-3 text-left transition-colors ${s.cls} ${filter === k ? 'ring-2 ring-offset-1 ring-[#1a5276]' : ''}`} data-testid={`boekhouder-check-count-${k}`}>
                  <p className="text-xs flex items-center gap-1"><s.icon className="w-3.5 h-3.5" /> {s.label}</p>
                  <p className="text-2xl font-bold">{result.counts[k]}</p>
                </button>
              ))}
              <div className="rounded-lg border border-gray-200 p-3 text-xs text-gray-600 space-y-0.5">
                <p><span className="font-semibold">{result.documents.uitgaven}</span> uitgaven</p>
                <p><span className="font-semibold">{result.documents.inkoop}</span> inkoopfacturen</p>
                <p><span className="font-semibold">{result.documents.facturen}</span> facturen · <span className="font-semibold">{result.documents.facturen_derden}</span> van derden</p>
                {result.documents.concepten > 0 && <p className="text-sky-700"><span className="font-semibold">{result.documents.concepten}</span> concept(en)</p>}
              </div>
            </div>

            {result.issues.length === 0 ? (
              <p className="text-sm text-emerald-700 flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="boekhouder-check-ok"><CheckCircle2 className="w-4 h-4" /> Alles compleet — geen ontbrekende gegevens gevonden in deze periode.</p>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 text-xs text-gray-600">
                  <span>{issues.length} punt(en){filter !== 'all' ? ` · filter: ${SEV[filter].label}` : ''}</span>
                  {filter !== 'all' && <button type="button" className="underline" onClick={() => setFilter('all')} data-testid="boekhouder-check-filter-reset">Alles tonen</button>}
                </div>
                <ul className="divide-y max-h-[28rem] overflow-y-auto" data-testid="boekhouder-check-issues">
                  {issues.map((x, i) => (
                    <li key={`${x.type}-${x.id}-${i}`} className="px-3 py-2 flex items-start gap-3 text-sm hover:bg-gray-50" data-testid={`boekhouder-check-issue-${i}`}>
                      <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${SEV[x.severity]?.dot}`} title={x.severity} />
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-800"><span className="text-xs font-semibold uppercase text-gray-500 mr-2">{TYPE_LABEL[x.type] || x.type}</span>{x.problem}</p>
                        <p className="text-xs text-gray-500 truncate" title={x.label}>{x.label}</p>
                      </div>
                      <Link to={x.link} className="text-xs text-[#1a5276] hover:underline flex items-center gap-1 shrink-0" data-testid={`boekhouder-check-link-${i}`}>{readOnly ? 'Bekijken' : 'Corrigeren'} <ExternalLink className="w-3 h-3" /></Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-gray-500">Dezelfde controle wordt als tabblad <strong>Controle</strong> meegenomen in het Excel van de kwartaal-export.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
